import React, { useState } from 'react';
import { syncToProd } from '../lib/api';

/**
 * One-click sync of preview MongoDB to production via the production admin API.
 * Lives in the admin dashboard header.
 */
const SyncToProdButton = ({ username }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => { setPassword(''); setBusy(false); setResult(null); setError(''); };
  const close = () => { setOpen(false); reset(); };

  const run = async () => {
    if (!password) { setError('Password required'); return; }
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await syncToProd(username, password);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Sync failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="sync-to-prod-open"
        onClick={() => setOpen(true)}
        className="px-3 py-1 text-sm border-2 border-gold bg-gold/10 text-dark-green hover:bg-gold hover:text-cream transition-colors tracking-wider"
        style={{ fontFamily: 'Playfair Display, serif' }}
        title="Push all preview changes to chimera-gallery.com"
      >
        ↑ Push to Production
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-charcoal/80 backdrop-blur-sm flex items-center justify-center p-6"
          data-testid="sync-to-prod-modal"
        >
          <div className="bg-ivory border-2 border-gold max-w-lg w-full p-8 shadow-2xl">
            <h2 className="text-2xl text-dark-green mb-2 tracking-widest" style={{ fontFamily: 'Playfair Display, serif' }}>
              Push to Production
            </h2>
            <p className="text-sm text-dark-green/70 mb-6 leading-relaxed">
              This will upsert every artwork, antique and artist from <em>preview</em> onto{' '}
              <strong>chimera-gallery.com</strong> — items present on both will be updated, new items
              will be added. Existing production items are never deleted.
            </p>

            {!result && (
              <>
                <label className="block mb-4">
                  <span className="text-xs uppercase tracking-wider text-dark-green/70">
                    Confirm your admin password ({username})
                  </span>
                  <input
                    data-testid="sync-to-prod-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !busy && run()}
                    autoFocus
                    className="w-full mt-1 p-2 bg-cream border-2 border-gold/30 focus:border-dark-crimson outline-none text-dark-green"
                  />
                </label>
                {error && (
                  <div data-testid="sync-to-prod-error" className="mb-4 p-3 bg-dark-crimson/10 border border-dark-crimson text-dark-crimson text-sm">
                    {error}
                  </div>
                )}
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    data-testid="sync-to-prod-cancel"
                    onClick={close}
                    disabled={busy}
                    className="px-4 py-2 text-sm border border-dark-green/40 text-dark-green hover:bg-dark-green/5"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="sync-to-prod-run"
                    onClick={run}
                    disabled={busy || !password}
                    className="px-5 py-2 text-sm bg-dark-green text-cream hover:bg-dark-crimson disabled:opacity-50 tracking-wider"
                  >
                    {busy ? 'Pushing…' : 'Push now'}
                  </button>
                </div>
              </>
            )}

            {result && (
              <div data-testid="sync-to-prod-result">
                <div className="mb-4 p-4 bg-dark-green/5 border-l-4 border-dark-green text-dark-green text-sm space-y-1 font-mono">
                  <div>Antiques · added <strong>{result.antiques_added}</strong> · updated <strong>{result.antiques_updated}</strong></div>
                  <div>Gallery  · added <strong>{result.gallery_added}</strong> · updated <strong>{result.gallery_updated}</strong></div>
                  <div>Artists  · added <strong>{result.artists_added}</strong> · updated <strong>{result.artists_updated}</strong></div>
                </div>
                {result.errors?.length > 0 && (
                  <div className="mb-4 p-3 bg-dark-crimson/10 border border-dark-crimson text-dark-crimson text-xs max-h-40 overflow-y-auto">
                    <div className="font-semibold mb-1">{result.errors.length} error(s):</div>
                    {result.errors.map((e, i) => <div key={i}>· {e}</div>)}
                  </div>
                )}
                {(!result.errors || result.errors.length === 0) && (
                  <p className="text-sm text-dark-green mb-4">
                    ✓ All clean. Production now matches preview.
                  </p>
                )}
                <div className="flex justify-end">
                  <button
                    data-testid="sync-to-prod-done"
                    onClick={close}
                    className="px-5 py-2 text-sm bg-dark-green text-cream hover:bg-dark-crimson tracking-wider"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SyncToProdButton;
